import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Button,
  CircularProgress,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import crLogo from "../../assets/images/icons/catering-rewards/cr-logo-black.png";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";

const CAMPAIGN_ERROR_CODES = {
  NEW_CUSTOMERS_ONLY: "CAMPAIGN_NEW_CUSTOMERS_ONLY",
  RETURNING_CUSTOMERS_ONLY: "CAMPAIGN_RETURNING_CUSTOMERS_ONLY",
  EMAIL_NOT_ELIGIBLE: "CAMPAIGN_EMAIL_NOT_ELIGIBLE",
};

const isCampaignTargetingError = (code) =>
  code === CAMPAIGN_ERROR_CODES.NEW_CUSTOMERS_ONLY ||
  code === CAMPAIGN_ERROR_CODES.RETURNING_CUSTOMERS_ONLY ||
  code === CAMPAIGN_ERROR_CODES.EMAIL_NOT_ELIGIBLE;

const MODAL_CONFIG = {
  [CAMPAIGN_ERROR_CODES.NEW_CUSTOMERS_ONLY]: {
    title: "You're already a member",
    body: "Looks like you already have an account with us \u2014 great news! This particular offer is not available to you, but your loyalty rewards are still active and every order keeps earning you reward points.",
    linkText: "View our rewards program",
    buttonText: "Continue",
  },
  [CAMPAIGN_ERROR_CODES.RETURNING_CUSTOMERS_ONLY]: {
    title: "Welcome \u2014 let's get you started",
    body: "This particular offer isn't available right now, but that won't stop you from earning rewards. Sign up today and start earning reward points on every order \u2014 no strings attached.",
    linkText: "See how our rewards program works",
    buttonText: "Sign up with regular rewards",
  },
  [CAMPAIGN_ERROR_CODES.EMAIL_NOT_ELIGIBLE]: {
    title: "This offer isn't available",
    body: "This particular offer is not available for your account, but you can still earn rewards on every order.",
    linkText: "View our rewards program",
    buttonText: "Continue",
  },
};

const buildLandingPageUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete("code");
  return url.toString();
};

const CampaignTargetingModal = ({ open, onClose, errorCode, onContinue }) => {
  const [loading, setLoading] = useState(false);
  const config = MODAL_CONFIG[errorCode];

  if (!config) return null;

  const handleContinue = async () => {
    setLoading(true);
    try {
      await onContinue();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ p: 0, textAlign: "center" }}>
        <Box mt={2}>
          <img src={crLogo} alt="Logo" className="mx-auto" style={{ width: 150, height: "auto" }} />
        </Box>

        <MDTypography
          variant="h2"
          sx={{
            fontSize: { xs: "1.25rem", md: "1.5rem" },
            fontWeight: 700,
            color: "#1A202C",
            mt: 1.5,
            mb: 1,
            textTransform: "none",
            letterSpacing: "-0.02em",
          }}
        >
          {config.title}
        </MDTypography>

        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <MDBox textAlign="center" px={2} pb={3}>
          <MDTypography
            variant="body2"
            sx={{ color: "#4A5568", mb: 1.5, fontSize: "0.95rem", lineHeight: 1.6 }}
          >
            {config.body}
          </MDTypography>

          <Link
            href={buildLandingPageUrl()}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "inline-block",
              mb: 3,
              fontSize: "0.9rem",
              color: "#107948",
              fontWeight: 500,
              textDecoration: "underline",
              "&:hover": { color: "#0d6038" },
            }}
          >
            {config.linkText}
          </Link>

          <Button
            variant="contained"
            fullWidth
            disabled={loading}
            onClick={handleContinue}
            sx={{
              backgroundColor: "#107948",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1rem",
              borderRadius: "8px",
              py: 1.2,
              "&:hover": { backgroundColor: "#0d6038" },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : config.buttonText}
          </Button>
        </MDBox>
      </DialogContent>
    </Dialog>
  );
};

CampaignTargetingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  errorCode: PropTypes.string,
  onContinue: PropTypes.func.isRequired,
};

export { CAMPAIGN_ERROR_CODES, isCampaignTargetingError };
export default CampaignTargetingModal;
